import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Phone } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, type CallsListQuery } from '../api'
import { DispoBadge, DispoMismatchBadge } from '../components/DispoBadge'
import {
  FilterBar,
  type FilterState,
  filtersToQuery,
} from '../components/FilterBar'
import {
  KeyboardShortcuts,
  ShortcutHint,
  type ShortcutMap,
} from '../components/KeyboardShortcuts'
import { ScoreBar } from '../components/ScoreBar'
import type { CallRow } from '../types'

const PAGE_SIZE = 50

// Mirrors aggregate.py METRIC_ORDER. Keep in sync if a metric is added/renamed
// in src/killacoach/scoring/aggregate.py.
const FAILING_METRIC_OPTIONS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'talk_ratio', label: 'Talk ratio' },
  { id: 'longest_monologue', label: 'Longest monologue' },
  { id: 'next_step_commitment', label: 'Next-step commitment' },
  { id: 'objection_delta', label: 'Objection delta' },
  { id: 'dispo_alignment', label: 'Dispo alignment' },
  { id: 'localize_within_15s', label: 'Localize within 15s' },
  { id: 'free_word_density', label: 'Free word density' },
  { id: 'time_to_pitch', label: 'Time to pitch' },
  { id: 'name_personalization_rate', label: 'Name personalization' },
  { id: 'seven_step_coverage', label: 'Seven-step coverage' },
]

const FAILING_METRIC_IDS: ReadonlySet<string> = new Set(
  FAILING_METRIC_OPTIONS.map((m) => m.id)
)

function readFilters(sp: URLSearchParams): FilterState {
  return {
    agent: sp.get('agent') ?? '',
    dispo: sp.get('dispo') ?? '',
    min_overall: sp.get('min_overall') ?? '',
    dispo_mismatch: sp.get('dispo_mismatch') === 'true',
    since: sp.get('since') ?? '',
    until: sp.get('until') ?? '',
  }
}

function readFailing(sp: URLSearchParams): string {
  const v = sp.get('failing') ?? ''
  return FAILING_METRIC_IDS.has(v) ? v : ''
}

function writeFilters(f: FilterState, offset: number, failing: string): URLSearchParams {
  const sp = new URLSearchParams()
  if (f.agent) sp.set('agent', f.agent)
  if (f.dispo) sp.set('dispo', f.dispo)
  if (f.min_overall) sp.set('min_overall', f.min_overall)
  if (f.dispo_mismatch) sp.set('dispo_mismatch', 'true')
  if (f.since) sp.set('since', f.since)
  if (f.until) sp.set('until', f.until)
  if (failing && FAILING_METRIC_IDS.has(failing)) sp.set('failing', failing)
  if (offset > 0) sp.set('offset', String(offset))
  return sp
}

export function CallsPage() {
  const [sp, setSp] = useSearchParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const filters = useMemo(() => readFilters(sp), [sp])
  const failing = useMemo(() => readFailing(sp), [sp])
  const offset = Number(sp.get('offset') ?? '0') || 0
  const [activeRow, setActiveRow] = useState(0)

  // Pull agent list for the dropdown — optional, fails silently.
  const agentsQ = useQuery({
    queryKey: ['agents', 'list'],
    queryFn: () => api.agents.list(),
    staleTime: 5 * 60_000,
  })

  const query: CallsListQuery = {
    ...filtersToQuery(filters),
    limit: PAGE_SIZE,
    offset,
  }

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['calls', 'list', query],
    queryFn: () => api.calls.list(query),
  })

  // TODO(C9): backend /api/calls?failing=METRIC support — once the route in
  // src/killacoach/api/routes/calls.py accepts a `failing` query param (or the
  // list response gains a `failed_metrics: string[]`), pipe `failing` through
  // `query` here and drop this client-side note. Until then the dropdown is a
  // visible affordance that round-trips via the URL but does not narrow rows.
  const items = data?.items ?? []
  const total = data?.total ?? 0

  const active = items[activeRow]

  // Clamp active row when data resets.
  useEffect(() => {
    setActiveRow((r) => Math.min(r, Math.max(items.length - 1, 0)))
  }, [items.length])

  const patchFilters = useCallback(
    (next: FilterState) => {
      setSp(writeFilters(next, 0, failing))
      setActiveRow(0)
    },
    [setSp, failing]
  )

  const patchFailing = useCallback(
    (next: string) => {
      setSp(writeFilters(filters, 0, next))
      setActiveRow(0)
    },
    [setSp, filters]
  )

  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['calls', 'list'] })
  }, [qc])

  // See note in CallDetail.tsx — hoist handlers out of the keymap object.
  const handleArrowDown = useCallback(
    () => setActiveRow((r) => Math.min(r + 1, Math.max(items.length - 1, 0))),
    [items.length]
  )
  const handleArrowUp = useCallback(() => setActiveRow((r) => Math.max(r - 1, 0)), [])
  const handleEnter = useCallback(
    () => {
      if (active) navigate(`/calls/${active.id}`)
    },
    [active, navigate]
  )
  const handleRefresh = useCallback(() => {
    refresh()
    void refetch()
  }, [refresh, refetch])

  const shortcuts: ShortcutMap = useMemo(
    () => ({
      ArrowDown: handleArrowDown,
      ArrowUp: handleArrowUp,
      Enter: handleEnter,
      r: handleRefresh,
    }),
    [handleArrowDown, handleArrowUp, handleEnter, handleRefresh]
  )

  const activeRowRef = useRef<HTMLTableRowElement>(null)
  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [])

  return (
    <div className="space-y-4">
      <KeyboardShortcuts shortcuts={shortcuts} />

      <header className="flex items-center gap-2">
        <Phone className="h-5 w-5 text-indigo-400" />
        <h1 className="text-xl font-semibold">Calls</h1>
        <span className="ml-2 text-xs text-slate-500">
          {isLoading ? '...' : `${total} total`}
        </span>
        <div className="ml-auto">
          <ShortcutHint
            keys={[
              ['↑↓', 'navigate'],
              ['⏎', 'open'],
              ['R', 'refresh'],
            ]}
          />
        </div>
      </header>

      <FilterBar
        state={filters}
        onChange={patchFilters}
        onRefresh={() => {
          refresh()
          void refetch()
        }}
        agents={agentsQ.data?.map((a) => ({
          vici_user: a.vici_user,
          full_name: a.full_name,
        }))}
      />

      <FailingMetricFilter value={failing} onChange={patchFailing} />

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-900 bg-red-950/40 p-3 text-sm text-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <span>{(error as Error).message}</span>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-slate-800 bg-slate-900/40">
        {isLoading ? (
          <div className="space-y-1 p-3">
            {Array.from({ length: 8 }, (_, i) => `skeleton-${i}`).map((key) => (
              <div key={key} className="h-10 animate-pulse rounded bg-slate-800/60" />
            ))}
          </div>
        ) : items.length === 0 ? (
          failing ? <FailingEmptyState metricId={failing} /> : <EmptyState />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/80 text-xs text-slate-400">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Agent</th>
                  <th className="px-3 py-2 text-left">Dispo</th>
                  <th className="px-3 py-2 text-left">AI Dispo</th>
                  <th className="px-3 py-2 text-right">Length</th>
                  <th className="px-3 py-2 text-left">Overall</th>
                  <th className="px-3 py-2 text-left">Metrics</th>
                  <th className="px-3 py-2 text-left">Alignment</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c, idx) => (
                  <CallRowView
                    key={c.id}
                    call={c}
                    active={idx === activeRow}
                    rowRef={idx === activeRow ? activeRowRef : undefined}
                    onClick={() => setActiveRow(idx)}
                    onOpen={() => navigate(`/calls/${c.id}`)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && total > PAGE_SIZE && (
          <Pager
            offset={offset}
            total={total}
            limit={PAGE_SIZE}
            onChange={(next) => {
              const spn = writeFilters(filters, next, failing)
              setSp(spn)
              setActiveRow(0)
            }}
          />
        )}
      </div>
    </div>
  )
}

function CallRowView({
  call,
  active,
  onClick,
  onOpen,
  rowRef,
}: {
  call: CallRow
  active: boolean
  onClick: () => void
  onOpen: () => void
  rowRef?: React.Ref<HTMLTableRowElement>
}) {
  return (
    <tr
      ref={rowRef}
      onClick={onClick}
      onDoubleClick={onOpen}
      className={`cursor-pointer border-t border-slate-800 transition-colors ${
        active ? 'bg-indigo-900/30 ring-1 ring-inset ring-indigo-700/40' : 'hover:bg-slate-800/40'
      }`}
    >
      <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-400">
        {new Date(call.call_started_at).toLocaleString()}
      </td>
      <td className="px-3 py-2">
        <span className="font-medium text-slate-200">{call.vici_user}</span>
        {call.agent_name && (
          <span className="ml-1.5 text-xs text-slate-500">{call.agent_name}</span>
        )}
      </td>
      <td className="px-3 py-2">
        <DispoBadge dispo={call.vici_disposition} size="md" />
      </td>
      <td className="px-3 py-2">
        {call.dispo_alignment?.predicted ? (
          <DispoBadge dispo={call.dispo_alignment.predicted} size="md" />
        ) : (
          <span className="text-xs text-slate-600">--</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-xs text-slate-400">
        {fmtLen(call.length_sec)}
      </td>
      <td className="px-3 py-2">
        <ScoreBar value={call.overall} />
      </td>
      <td className="px-3 py-2 font-mono text-xs text-slate-400">
        {call.metrics_passed ?? '--'}/5
      </td>
      <td className="px-3 py-2">
        <DispoMismatchBadge alignment={call.dispo_alignment} />
      </td>
    </tr>
  )
}

function fmtLen(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function Pager({
  offset,
  total,
  limit,
  onChange,
}: {
  offset: number
  total: number
  limit: number
  onChange: (next: number) => void
}) {
  const page = Math.floor(offset / limit) + 1
  const pages = Math.max(1, Math.ceil(total / limit))
  const prev = Math.max(0, offset - limit)
  const next = Math.min((pages - 1) * limit, offset + limit)
  return (
    <div className="flex items-center justify-between border-t border-slate-800 bg-slate-900/60 px-3 py-2 text-xs text-slate-400">
      <span>
        Page {page} of {pages} — {total} calls
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => onChange(prev)}
          className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-slate-300 disabled:opacity-40 enabled:hover:bg-slate-800"
        >
          Prev
        </button>
        <button
          type="button"
          disabled={page >= pages}
          onClick={() => onChange(next)}
          className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-slate-300 disabled:opacity-40 enabled:hover:bg-slate-800"
        >
          Next
        </button>
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="p-10 text-center">
      <p className="text-sm text-slate-400">
        No calls yet. Run{' '}
        <code className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-xs text-slate-200">
          python -m killacoach.runner.cli --agent 1201 --limit 50
        </code>
        .
      </p>
    </div>
  )
}

function FailingEmptyState({ metricId }: { metricId: string }) {
  const label =
    FAILING_METRIC_OPTIONS.find((m) => m.id === metricId)?.label ?? metricId
  return (
    <div className="p-10 text-center">
      <p className="text-sm text-slate-400">
        No calls failing <span className="font-medium text-slate-200">{label}</span>.
      </p>
      <p className="mt-1 text-xs text-slate-500">
        Note: this filter currently round-trips through the URL but is not yet
        applied server-side — see TODO(C9) in CallsPage.tsx.
      </p>
    </div>
  )
}

function FailingMetricFilter({
  value,
  onChange,
}: {
  value: string
  onChange: (next: string) => void
}) {
  return (
    <label
      className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-sm text-slate-300"
      title="Requires API support — see routes/calls.py. Round-trips via ?failing= URL param today; does not yet narrow rows."
    >
      <span className="text-[10px] uppercase tracking-wide text-slate-500">
        Failing metric
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-56 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-200"
      >
        <option value="">All calls</option>
        {FAILING_METRIC_OPTIONS.map((m) => (
          <option key={m.id} value={m.id}>
            {m.label}
          </option>
        ))}
      </select>
      {value && (
        <span className="text-xs text-amber-400/80">
          pending backend support
        </span>
      )}
    </label>
  )
}
